import { useEffect, useMemo, useState } from 'react';
import './styles/fit-live.css';
import './styles/fit-live-public.css';
import { WEEKDAY_LABELS } from './constants';
import {
  DEFAULT_CLASS_IMAGE,
  classToPreview,
  creatorToTrainer,
  fetchPublicDirectory,
  levelLabel,
  scheduleFromClasses,
  waUrl,
  weekColumns,
} from './creatorDirectory';
import FitLiveGateModal from './FitLiveGateModal';
import FitLiveClassDetail from './FitLiveClassDetail';
import { applyDocumentFavicon } from '../utils/documentBrand';
import { ECOSISTEMA_FIT_FAVICON, ECOSISTEMA_FIT_LOGO } from '../utils/fitLiveModule';

const DEFAULT_BLURB = 'Clases en vivo y replays con entrenadores de Ecosistema Fit. Entra, explora y reserva cuando quieras.';

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function FitLivePublicLanding() {
  const [mainTab, setMainTab] = useState('trainers'); // trainers | calendar
  const [trainerId, setTrainerId] = useState('');
  const [calFilter, setCalFilter] = useState('all');
  const [creators, setCreators] = useState([]);
  const [classes, setClasses] = useState([]);
  const [replays, setReplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [gate, setGate] = useState({ open: false, reason: 'class' });
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [supportWa, setSupportWa] = useState('');

  useEffect(() => {
    applyDocumentFavicon(ECOSISTEMA_FIT_FAVICON, { brand_name: 'Ecosistema Fit' });
    document.title = 'Ecosistema Fit';
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPublicDirectory()
      .then((data) => {
        if (cancelled) return;
        setCreators(data.creators);
        setClasses(data.classes);
        setReplays(data.replays);
        setSupportWa(data.supportWhatsapp);
        setErr('');
      })
      .catch(() => {
        if (!cancelled) {
          setErr('Catálogo no disponible por ahora. Reintenta en unos minutos.');
          setCreators([]);
          setClasses([]);
          setReplays([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const trainers = useMemo(() => creators.map(creatorToTrainer), [creators]);
  const trainer = useMemo(
    () => trainers.find((t) => t.id === trainerId) || trainers[0] || null,
    [trainers, trainerId],
  );

  const trainerClasses = useMemo(
    () => (trainer ? classes.filter((c) => c.creator_id === trainer.id) : []),
    [classes, trainer],
  );
  const schedule = useMemo(() => scheduleFromClasses(trainerClasses), [trainerClasses]);
  const weekDays = useMemo(() => weekColumns(schedule), [schedule]);
  const previews = useMemo(() => trainerClasses.map(classToPreview), [trainerClasses]);
  const liveClasses = useMemo(() => trainerClasses.filter((c) => c.tipo !== 'replay'), [trainerClasses]);
  const trainerReplays = useMemo(
    () => (trainer ? replays.filter((c) => c.creator_id === trainer.id) : []),
    [replays, trainer],
  );

  const globalWeek = useMemo(() => {
    const scoped = calFilter === 'all' ? classes : classes.filter((c) => c.creator_id === calFilter);
    return weekColumns(scheduleFromClasses(scoped));
  }, [classes, calFilter]);

  const openGate = (reason = 'class') => setGate({ open: true, reason });

  const selectTrainer = (id) => {
    setTrainerId(id);
    setMainTab('trainers');
    window.setTimeout(() => scrollToId('entrenador'), 50);
  };

  const openPreview = (pv) => {
    setSelectedPreview(pv);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openSlot = (slot) => {
    const row = classes.find((c) => c.id === slot.classId);
    if (row) openPreview(classToPreview(row));
  };

  const contactHref = waUrl(supportWa, 'Hola, quiero info de Fit Live / Ecosistema Fit');
  const joinHref = waUrl(supportWa, 'Hola, soy entrenador y quiero ser Creator en Fit Live (clases en vivo y grabadas)');

  if (selectedPreview) {
    const t = trainers.find((x) => x.id === selectedPreview.trainerId) || trainer;
    return (
      <div className="fit-live-root fl-public" data-testid="fit-live-public">
        <FitLiveClassDetail
          classItem={selectedPreview}
          trainer={t}
          schedule={scheduleFromClasses(classes.filter((c) => c.creator_id === selectedPreview.trainerId))}
          onBack={() => setSelectedPreview(null)}
          onTryFree={() => openGate('class')}
          onLogin={() => openGate('login')}
        />
        <FitLiveGateModal
          open={gate.open}
          reason={gate.reason}
          trainerLabel={t?.label || ''}
          onClose={() => setGate((g) => ({ ...g, open: false }))}
        />
        {contactHref ? (
          <a className="fl-fab-wa" href={contactHref} target="_blank" rel="noreferrer">
            WhatsApp
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div className="fit-live-root fl-public" data-testid="fit-live-public">
      <header className="fl-pub-nav">
        <div className="fl-pub-brand">
          <img className="fl-pub-logo" src={ECOSISTEMA_FIT_LOGO} alt="Ecosistema Fit" />
          <div>
            <strong>FIT LIVE</strong>
            <span>Ecosistema Fit</span>
          </div>
        </div>
        <nav className="fl-pub-links" aria-label="Secciones">
          <button type="button" className="fl-pub-nav-btn" onClick={() => setMainTab('trainers')}>Entrenadores</button>
          <button type="button" className="fl-pub-nav-btn" onClick={() => setMainTab('calendar')}>Calendario</button>
          <a href="#clases">Clases</a>
          <a href="#trabaja-con-nosotros">Trabaja con nosotros</a>
        </nav>
        <div className="fl-pub-actions">
          <button type="button" className="fl-pub-ghost" onClick={() => openGate('login')}>
            Inicia sesión
          </button>
          <button type="button" className="fl-pub-solid" onClick={() => openGate('register')}>
            Únete
          </button>
        </div>
      </header>

      <div className="fl-pub-main-tabs" role="tablist" aria-label="Vista Fit Live">
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === 'trainers'}
          className={mainTab === 'trainers' ? 'is-active' : ''}
          onClick={() => setMainTab('trainers')}
        >
          Entrenadores
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === 'calendar'}
          className={mainTab === 'calendar' ? 'is-active' : ''}
          onClick={() => setMainTab('calendar')}
        >
          Calendario
        </button>
      </div>

      <section className="fl-intro" id="que-es">
        <div className="fl-intro-copy">
          <p className="fl-intro-kicker">Qué es Ecosistema Fit</p>
          <h2>Una sola cuenta para entrenar en vivo, nutrirte y seguir a tu coach.</h2>
          <p>
            Ecosistema Fit reúne Fit Live (clases en vivo y replays con tokens), planes de alimentación
            y el acompañamiento de entrenadores en un mismo lugar. Eliges al profesional que se adapta
            a ti y reservas cuando quieras.
          </p>
          <ul>
            <li>Clases en vivo y calendario por entrenador</li>
            <li>Replays y FitTokens para pagar solo lo que usas</li>
            <li>WhatsApp de soporte</li>
          </ul>
          <button type="button" className="fl-pub-solid" onClick={() => openGate('register')}>
            Crea tu cuenta
          </button>
        </div>
        <div className="fl-intro-video">
          <iframe
            title="Qué es Ecosistema Fit"
            src="https://www.youtube.com/embed/2e-hsoNlCnM"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </section>

      {mainTab === 'calendar' ? (
        <main className="fl-pub-main">
          <section id="calendario-general" className="fl-trainer-panel">
            <div className="fl-section-head">
              <div>
                <h2>Calendario general</h2>
                <p>Próximas clases en vivo de todos los entrenadores. Filtra por entrenador.</p>
              </div>
              <label className="fl-cal-filter">
                Entrenador
                <select value={calFilter} onChange={(e) => setCalFilter(e.target.value)}>
                  <option value="all">Todos</option>
                  {trainers.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="fl-week-cal" role="grid" aria-label="Calendario general">
              {globalWeek.map((col) => (
                <div key={col.day} className="fl-week-col" role="gridcell">
                  <div className="fl-week-day">{WEEKDAY_LABELS[col.day]}</div>
                  <div className="fl-week-slots">
                    {col.slots.length === 0 ? (
                      <p className="fl-week-empty">—</p>
                    ) : col.slots.map((slot) => (
                      <button
                        key={slot.classId}
                        type="button"
                        className="fl-week-slot"
                        onClick={() => openSlot(slot)}
                      >
                        <strong>{slot.time}</strong>
                        <span>{slot.title}</span>
                        <em>
                          {slot.creatorName}
                          {' · '}
                          {slot.duration}
                          {' '}
                          min
                        </em>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {!loading && globalWeek.every((col) => col.slots.length === 0) ? (
              <p className="fl-note">Aún no hay clases programadas.</p>
            ) : null}
          </section>
        </main>
      ) : (
        <>
          <section id="conoce-entrenadores" className="fl-pub-main">
            <div className="fl-section-head">
              <div>
                <h2>Conoce nuestros entrenadores</h2>
                <p>Ordenados por la asistencia real a sus clases en los últimos 30 días.</p>
              </div>
            </div>
            {err ? <p className="fl-note">{err}</p> : null}
            {loading ? <p className="fl-note">Cargando entrenadores…</p> : null}
            {!loading && !err && trainers.length === 0 ? (
              <p className="fl-note">
                Muy pronto presentaremos a nuestros entrenadores. ¿Eres entrenador?
                {' '}
                <button type="button" className="fl-pub-link fl-pub-link--light" onClick={() => scrollToId('trabaja-con-nosotros')}>
                  Trabaja con nosotros.
                </button>
              </p>
            ) : null}
          </section>

          {trainer ? (
            <>
              <div className="fl-pub-disciplines" role="tablist" aria-label="Entrenadores">
                {trainers.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={trainer.id === t.id}
                    className={`fl-pub-disc${trainer.id === t.id ? ' is-active' : ''}`}
                    onClick={() => selectTrainer(t.id)}
                  >
                    <span className="fl-pub-disc-icon" aria-hidden>
                      <img src={t.logo} alt="" width={72} height={72} />
                    </span>
                    {t.label}
                    {t.isNew ? ' · Nuevo' : ''}
                    <p className="fl-pub-disc-caption">{t.caption}</p>
                  </button>
                ))}
              </div>

              <section
                className="fl-pub-hero"
                style={{ backgroundImage: `linear-gradient(180deg, rgba(28,25,23,.25), rgba(28,25,23,.72)), url(${DEFAULT_CLASS_IMAGE})` }}
              >
                <div className="fl-pub-hero-inner">
                  <h1>{trainer.label}</h1>
                  <p>{trainer.bio || DEFAULT_BLURB}</p>
                  <div className="fl-pub-hero-cta">
                    <button type="button" className="fl-pub-solid" onClick={() => scrollToId('entrenador')}>
                      Ver programación
                    </button>
                    <button type="button" className="fl-pub-outline" onClick={() => scrollToId('previews')}>
                      ▶ Explorar clases
                    </button>
                  </div>
                  <p className="fl-pub-member">
                    ¿Ya eres miembro?
                    {' '}
                    <button type="button" className="fl-pub-link" onClick={() => openGate('login')}>
                      Inicia sesión aquí.
                    </button>
                  </p>
                </div>
              </section>

              <main className="fl-pub-main">
                <section id="entrenador" className="fl-trainer-panel">
                  <div className="fl-trainer-head">
                    <img className="fl-trainer-logo" src={trainer.logo} alt={trainer.label} width={88} height={88} />
                    <div>
                      <p className="fl-trainer-kicker">Entrenador Fit Live</p>
                      <h2>{trainer.label}</h2>
                      {trainer.bio ? <p className="fl-trainer-bio">{trainer.bio}</p> : null}
                      <p className="fl-trainer-spec">{trainer.specialty}</p>
                    </div>
                  </div>

                  <div className="fl-section-head" style={{ marginTop: '1.5rem' }}>
                    <div>
                      <h3 className="fl-trainer-cal-title">Programación</h3>
                      <p>Próximas clases en vivo. Toca una clase para ver el detalle.</p>
                    </div>
                    <span className="fl-banner-phase">{schedule.length} próximas</span>
                  </div>

                  <div className="fl-week-cal" role="grid" aria-label="Calendario semanal">
                    {weekDays.map((col) => (
                      <div key={col.day} className="fl-week-col" role="gridcell">
                        <div className="fl-week-day">{WEEKDAY_LABELS[col.day]}</div>
                        <div className="fl-week-slots">
                          {col.slots.length === 0 ? (
                            <p className="fl-week-empty">—</p>
                          ) : col.slots.map((slot) => (
                            <button
                              key={slot.classId}
                              type="button"
                              className="fl-week-slot"
                              onClick={() => openSlot(slot)}
                            >
                              <strong>{slot.time}</strong>
                              <span>{slot.title}</span>
                              <em>
                                {slot.duration}
                                {' '}
                                min ·
                                {slot.level}
                                {' · '}
                                {slot.tokens}
                                {' '}
                                tok
                              </em>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section id="previews">
                  <div className="fl-section-head">
                    <div>
                      <h2>Explora sus clases</h2>
                      <p>Al hacer clic abres el detalle de la clase.</p>
                    </div>
                    <span className="fl-banner-phase">{previews.length} clases</span>
                  </div>
                  <div className="fl-preview-grid">
                    {previews.map((pv) => (
                      <button
                        key={pv.id}
                        type="button"
                        className="fl-preview-card"
                        onClick={() => openPreview(pv)}
                      >
                        <div className="fl-preview-media">
                          <img src={pv.image} alt="" loading="lazy" />
                          <span className="fl-preview-play" aria-hidden>▶</span>
                        </div>
                        <div className="fl-preview-body">
                          <p className="fl-preview-level">{pv.level}</p>
                          <h3>{pv.title}</h3>
                          <p className="fl-preview-meta">
                            {pv.coach}
                            {' · '}
                            {pv.discipline}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>

                <section id="clases">
                  <div className="fl-section-head">
                    <div>
                      <h2>Clases publicadas</h2>
                      <p>Reserva cuando inicies sesión.</p>
                    </div>
                    <span className="fl-banner-phase">{`${liveClasses.length} disponibles`}</span>
                  </div>
                  <div className="fl-grid fl-grid-live">
                    {liveClasses.map((c) => (
                      <article key={c.id} className="fl-card">
                        <div className="fl-card-media">
                          <img src={c.image_url || DEFAULT_CLASS_IMAGE} alt={c.title} loading="lazy" />
                        </div>
                        <div className="fl-card-body">
                          <div className="fl-level">{levelLabel(c.level)}</div>
                          <h3 className="fl-card-title">{c.title}</h3>
                          <p className="fl-card-meta">
                            {c.creator_name}
                            {c.fecha ? ` · ${c.fecha}` : ''}
                            {c.hora ? ` · ${c.hora}` : ''}
                          </p>
                          <div className="fl-card-foot">
                            <span className="fl-tokens">
                              <em>{c.price_tokens}</em>
                              {' '}
                              tokens
                            </span>
                            <button type="button" className="fl-cta fl-cta-accent" onClick={() => openGate('reserve')}>
                              Reservar
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}
                    {liveClasses.length === 0 ? (
                      <p className="fl-note">Este entrenador aún no tiene clases en vivo publicadas.</p>
                    ) : null}
                  </div>
                </section>

                <section id="replays">
                  <div className="fl-section-head">
                    <div>
                      <h2>Replays</h2>
                      <p>Bajo demanda · consume tokens al reproducir (con sesión)</p>
                    </div>
                  </div>
                  <div className="fl-grid">
                    {trainerReplays.map((c) => (
                      <article key={c.id} className="fl-card">
                        <div className="fl-card-media">
                          <img src={c.image_url || DEFAULT_CLASS_IMAGE} alt={c.title} loading="lazy" />
                        </div>
                        <div className="fl-card-body">
                          <div className="fl-level">Replay</div>
                          <h3 className="fl-card-title">{c.title}</h3>
                          <p className="fl-card-meta">{c.creator_name}</p>
                          <div className="fl-card-foot">
                            <span className="fl-tokens">
                              <em>{c.price_tokens}</em>
                              {' '}
                              tokens
                            </span>
                            <button type="button" className="fl-cta fl-cta-accent" onClick={() => openGate('replay')}>
                              Ver
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}
                    {trainerReplays.length === 0 ? (
                      <p className="fl-note">Este entrenador aún no tiene replays.</p>
                    ) : null}
                  </div>
                </section>
              </main>
            </>
          ) : null}
        </>
      )}

      <main className="fl-pub-main">
        <section id="trabaja-con-nosotros" className="fl-trainer-panel">
          <div className="fl-section-head">
            <div>
              <h2>Trabaja con nosotros</h2>
              <p>
                ¿Eres entrenador? Ecosistema Fit es tu vitrina: llega a más personas, publica con tu marca
                y cobra por lo que ya sabes hacer.
              </p>
            </div>
          </div>
          <ul className="fl-join-list">
            <li>
              Programa clases en vivo y clases grabadas cuando quieras: tú defines el calendario,
              el precio y el ritmo.
            </li>
            <li>
              Comercializa tu contenido en una plataforma hecha para exponerte: más visibilidad,
              más reservas, más alumnos que te descubren.
            </li>
            <li>
              Tú te quedas con el 80 % del ticket de entrada a tus clases; la plataforma solo
              retiene el 20 % por la operación y la audiencia.
            </li>
          </ul>
          {joinHref ? (
            <a className="fl-pub-solid fl-join-cta" href={joinHref} target="_blank" rel="noreferrer">
              Quiero ser Creator — escríbenos
            </a>
          ) : null}
        </section>
      </main>

      <FitLiveGateModal
        open={gate.open}
        reason={gate.reason}
        trainerLabel={trainer?.label || ''}
        onClose={() => setGate((g) => ({ ...g, open: false }))}
      />

      {contactHref ? (
        <a className="fl-fab-wa" href={contactHref} target="_blank" rel="noreferrer" title="WhatsApp soporte">
          WhatsApp
        </a>
      ) : null}
    </div>
  );
}
